import type {
	CustomFormLocale,
	ICustomFormItem,
	ICustomFormItemRaw,
} from '../types';
import {generateCustomFormItemName} from './generateCustomFormItemName';
import {getCustomFormRawFromJson} from './getCustomFormRawFromJson';

const getUserValueName = (val: string | CustomFormLocale) =>
	typeof val === 'string' ? val : val.key;

const getItemValue = (
	name: string,
	item: ICustomFormItemRaw,
	values: Record<string, string | boolean>,
) => {
	if (item.type === 'checkboxes')
		return values[name] ?? item.userValue?.toString() === 'true';
	return values[name] ?? item.userValue != null ? item.userValue : '';
};

const getItemValues = (
	name: string,
	item: ICustomFormItemRaw,
	values: Record<string, string | boolean>,
) => {
	if (item.type === 'checkboxes') {
		return typeof item.value === 'string'
			? [item.value === 'true']
			: item.value.reduce<boolean[]>((accum, val) => {
					const userValue =
						values[`${name}-${getUserValueName(val)}`]?.toString() === 'true';
					return [...accum, userValue];
			  }, []);
	}
	return item.userValues != null ? item.userValues : [];
};

const parseCustomFormItem = (
	item: ICustomFormItemRaw,
	userValues: Record<string, any>,
) => {
	const name = item.label.key || generateCustomFormItemName(item.label.en);
	return {
		...item,
		disabled: item.disabled === 'disabled' || item.disabled === true,
		label: {
			...item.label,
			key: name,
		},
		name,
		required: item.required === 'required' || item.required === true,
		userValue: getItemValue(name, item, userValues),
		userValues: getItemValues(name, item, userValues),
	};
};

type ParseCustomFormDataJsonOptions = {
	duplicateKeys?: string[];
	userValues?: Record<string, any>;
};

export const parseCustomFormDataJson = (
	formData: string,
	{duplicateKeys = [], userValues = {}}: ParseCustomFormDataJsonOptions = {},
): ICustomFormItem[] => {
	try {
		const parsed = getCustomFormRawFromJson(formData);

		return parsed.reduce<ICustomFormItem[]>((accum, item) => {
			if (duplicateKeys.includes(item.label.key)) return accum;
			return [...accum, parseCustomFormItem(item, userValues)];
		}, []);
	} catch (ex) {
		return [];
	}
};

export default parseCustomFormDataJson;
